import { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { usePhoneMask } from '@/hooks/usePhoneMask';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isDark } = useTheme();
  const phoneMask = usePhoneMask();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = {
      nome: formData.get('nome'),
      empresa: formData.get('empresa'),
      email: formData.get('email'),
      telefone: formData.get('telefone'),
      segmento: formData.get('segmento'),
      descricao: formData.get('descricao'),
    };
    try {
      await fetch('https://tyuvxodvirkjoxeaflsn.supabase.co/functions/v1/submit-diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = isDark
    ? 'w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 transition-colors'
    : 'w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-cyan-400 transition-colors';

  const selectClass = isDark
    ? 'w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-white/10 text-slate-300 text-sm focus:outline-none focus:border-cyan-500/50 transition-colors cursor-pointer'
    : 'w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer';

  return (
    <section
      id="contato"
      className={`py-28 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#080d1a]' : 'bg-white'}`}
    >
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent to-transparent ${
        isDark ? 'via-cyan-500/30' : 'via-cyan-400/30'
      }`}></div>
      <div className={`absolute top-1/2 right-0 w-96 h-96 rounded-full blur-3xl pointer-events-none ${isDark ? 'bg-cyan-500/5' : 'bg-cyan-400/8'}`}></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border w-fit ${
                isDark ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-cyan-400/40 bg-cyan-50'
              }`}>
                <i className="ri-message-3-line text-cyan-500 text-sm"></i>
                <span className="text-cyan-600 text-xs font-semibold tracking-widest uppercase">Diagnóstico Gratuito</span>
              </div>
              <h2 className={`text-3xl lg:text-5xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Vamos construir o{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-cyan-400 bg-clip-text text-transparent">
                  sistema ideal
                </span>{' '}
                para você
              </h2>
              <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Conte-nos sobre o seu negócio e os desafios que enfrenta. Nossa equipe técnica fará um diagnóstico gratuito e apresentará a melhor solução para o seu caso.
              </p>
            </div>

            {/* Info cards */}
            <div className="flex flex-col gap-4">
              {[
                { icon: 'ri-time-line', title: 'Resposta em até 24h', desc: 'Nossa equipe retorna rapidamente com uma proposta inicial.' },
                { icon: 'ri-search-eye-line', title: 'Diagnóstico sem compromisso', desc: 'Analisamos seu processo e apresentamos soluções sem custo.' },
                { icon: 'ri-team-line', title: 'Equipe especializada', desc: 'Arquitetos de software, designers e especialistas de negócio.' },
              ].map((item) => (
                <div
                  key={item.title}
                  className={`flex items-start gap-4 p-4 rounded-xl border ${
                    isDark ? 'border-white/8 bg-[#0d1526]' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${
                    isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-200'
                  }`}>
                    <i className={`${item.icon} text-cyan-500 text-lg`}></i>
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</div>
                    <div className={`text-sm mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className={`rounded-2xl border p-8 ${
            isDark ? 'border-white/10 bg-[#0d1526]' : 'border-slate-200 bg-slate-50'
          }`}>
            {submitted ? (
              <div className="flex flex-col items-center justify-center gap-6 py-12 text-center">
                <div className={`w-16 h-16 flex items-center justify-center rounded-full ${
                  isDark ? 'bg-cyan-500/20 border border-cyan-500/30' : 'bg-cyan-100 border border-cyan-300'
                }`}>
                  <i className="ri-check-line text-cyan-500 text-3xl"></i>
                </div>
                <div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Mensagem enviada!</h3>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Nossa equipe entrará em contato em até 24 horas com um diagnóstico personalizado.</p>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                id="diagnostico-granato"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div>
                  <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Solicitar Diagnóstico</h3>
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Preencha o formulário e entraremos em contato.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Nome *</label>
                    <input name="nome" type="text" required placeholder="Seu nome completo" className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Empresa *</label>
                    <input name="empresa" type="text" required placeholder="Nome da empresa" className={inputClass} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>E-mail *</label>
                    <input name="email" type="email" required placeholder="seu@email.com" className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Telefone</label>
                    <input
                      name="telefone"
                      type="tel"
                      placeholder="(00) 00000-0000"
                      className={inputClass}
                      value={phoneMask.value}
                      onChange={phoneMask.handleChange}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Segmento</label>
                  <select name="segmento" className={selectClass}>
                    <option value="">Selecione seu segmento</option>
                    <option value="escola">Instituição de Ensino</option>
                    <option value="reciclagem">Empresa de Reciclagem</option>
                    <option value="industria">Indústria</option>
                    <option value="distribuidora">Distribuidora</option>
                    <option value="servicos">Prestador de Serviços</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Descreva seu projeto *</label>
                  <textarea
                    name="descricao"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Conte-nos sobre os desafios do seu negócio e o que você precisa automatizar ou melhorar..."
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg text-white font-semibold text-base whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Solicitar Diagnóstico Gratuito</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
